import { useEffect, useState, type ReactNode } from 'react';
import { Animated, Easing } from 'react-native';

import { usePrefersReducedMotion } from '@/hooks/use-reduced-motion';
import { rowEntrance } from '@/theme/motion';
import { easing } from '@/theme/tokens.generated';

// One-shot entrance for a list row: rise + fade, staggered by index. Runs on
// mount only, so scrolling a long list back up never replays it.
export function RiseIn({ index, children }: { index: number; children: ReactNode }) {
  const reduced = usePrefersReducedMotion();
  const spec = rowEntrance(index, reduced);
  const [progress] = useState(() => new Animated.Value(reduced ? 1 : 0));

  useEffect(() => {
    if (spec.duration === 0) {
      progress.setValue(1);
      return;
    }
    Animated.timing(progress, {
      toValue: 1,
      duration: spec.duration,
      delay: spec.delay,
      easing: Easing.bezier(...easing.easeOut),
      useNativeDriver: true,
    }).start();
    // mount-only on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Animated.View
      style={{
        opacity: progress,
        transform: [{ translateY: progress.interpolate({ inputRange: [0, 1], outputRange: [spec.distance, 0] }) }],
      }}
    >
      {children}
    </Animated.View>
  );
}
